import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ProgressIndicator from './components/ProgressIndicator';
import AttendeeInfoForm from './components/AttendeeInfoForm';
import PaymentForm from './components/PaymentForm';
import BookingSummary from './components/BookingSummary';
import PaymentConfirmation from './components/PaymentConfirmation';

const EventBookingFlow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [discountCode, setDiscountCode] = useState('EARLY20'); // Prefill with sample discount code
  const [discountApplied, setDiscountApplied] = useState(null);
  
  // Get event data from navigation state or use mock data
  const eventDataFromState = location?.state?.eventData;
  const selectedTierFromState = location?.state?.selectedTier;
  const quantityFromState = location?.state?.quantity || 1;

  // Mock event data if not provided - use actual database UUIDs
  const mockEventData = {
    id: '43a63562-b84a-46da-aaca-dbe7dc27cfbf', // Use actual UUID from database
    title: 'Tech Conference 2025',
    date: 'March 15, 2025',
    time: '9:00 AM - 6:00 PM',
    location: 'San Francisco Convention Center',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=400&h=300&fit=crop',
    organizer: {
      name: 'TechEvents Inc.',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face'
    }
  };

  const mockSelectedTier = {
    id: '64038dc1-fb8d-4387-bb7b-1c2ef2861fc0', // Use actual UUID from database
    name: 'General Admission',
    price: 299.00,
    benefits: [
      'All conference sessions',
      'Premium networking lunch',
      'VIP seating',
      'Welcome gift bag',
      'Priority Q&A access',
      'Digital resource library'
    ]
  };

  const eventData = eventDataFromState || mockEventData;
  const selectedTier = selectedTierFromState || mockSelectedTier;
  const quantity = quantityFromState;

  // Create sample data for prefilling forms
  const getSampleUserData = () => ({
    firstName: user?.first_name || 'John',
    lastName: user?.last_name || 'Smith', 
    email: user?.email || 'john.smith@example.com',
    phone: user?.phone || '+1 (555) 123-4567',
    address: '123 Main Street',
    city: 'San Francisco',
    state: 'CA',
    zipCode: '94102',
    country: 'us',
    company: 'Tech Solutions Inc.',
    specialRequirements: '',
    eventUpdates: true,
    marketingEmails: false,
    agreeToTerms: false,
    payment: {
      method: 'card',
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      cardName: user?.full_name || 'John Smith',
      billingAddress: '123 Main Street',
      billingCity: 'San Francisco',
      billingState: 'CA',
      billingZip: '94102'
    }
  });

  // Form data state - prefilled with sample data
  const [formData, setFormData] = useState(getSampleUserData());

  // Update form data when user authentication changes
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        firstName: user?.first_name || prev?.firstName,
        lastName: user?.last_name || prev?.lastName,
        email: user?.email || prev?.email,
        phone: user?.phone || prev?.phone,
        payment: {
          ...prev?.payment,
          cardName: user?.full_name || prev?.payment?.cardName
        }
      }));
    }
  }, [user]);

  // Enhanced redirect check with debugging
  useEffect(() => {
    if (!eventDataFromState && !selectedTierFromState && currentStep < 3) {
      console.log('🔧 [DEBUG] Using mock data for demo purposes');
      console.log('🔧 [DEBUG] Mock event data:', {
        eventId: mockEventData?.id,
        ticketId: mockSelectedTier?.id,
        uuidValidation: {
          eventId: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i?.test(mockEventData?.id),
          ticketId: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i?.test(mockSelectedTier?.id)
        }
      });
    }
  }, [eventDataFromState, selectedTierFromState, currentStep]);

  // Calculate total
  const basePrice = selectedTier?.price * quantity;
  const discountAmount = discountApplied 
    ? discountApplied?.type === 'percentage' 
      ? (basePrice * discountApplied?.discount) / 100
      : discountApplied?.discount
    : 0;
  const subtotal = basePrice - discountAmount;
  const processingFee = subtotal * 0.029;
  const total = subtotal + processingFee;

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <AttendeeInfoForm
            onNext={handleNextStep}
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 2:
        return (
          <PaymentForm
            onNext={handleNextStep}
            onBack={handlePrevStep}
            formData={formData}
            setFormData={setFormData}
            discountCode={discountCode}
            setDiscountCode={setDiscountCode}
            discountApplied={discountApplied}
            setDiscountApplied={setDiscountApplied}
            eventData={eventData}
            selectedTier={selectedTier}
            quantity={quantity}
            total={total}
          />
        );
      case 3:
        return (
          <PaymentConfirmation
            eventData={eventData}
            formData={formData}
            selectedTier={selectedTier}
            quantity={quantity}
            total={total}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      <div className="container mx-auto px-4 py-8">
        {/* Progress Indicator */}
        {currentStep < 3 && (
          <ProgressIndicator currentStep={currentStep} totalSteps={3} />
        )}

        <div className={`grid gap-8 ${currentStep < 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-1'}`}>
          {/* Main Content */}
          <div className={currentStep < 3 ? 'lg:col-span-2' : 'lg:col-span-1'}>
            {renderStepContent()}
          </div>

          {/* Booking Summary - Only show on steps 1 and 2 */}
          {currentStep < 3 && (
            <div className="lg:col-span-1">
              <BookingSummary
                eventData={eventData}
                selectedTier={selectedTier}
                quantity={quantity}
                discountApplied={discountApplied}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventBookingFlow;